// The tenancy's parking card (prototype §37 parkBlock): the bay that comes with the flat, since when, the
// vehicle — Release, or Allocate a free bay on the building's car park.
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Car } from 'lucide-react';
import { Card, Button, Field, Input, Select, Modal, KeyValue, DateText, SkeletonRows } from '../ui';
import { Can } from '../Can';
import { Confirm } from '../forms/common';
import { parkingApi } from '../../lib/parking';
import { PlanSnippet } from './PlanSnippet';

const today = () => new Date().toISOString().slice(0, 10);

export function TenancyParkingCard({ tenancyId, propertyId, live }: { tenancyId: string; propertyId: string; live: boolean }) {
  const qc = useQueryClient();
  const key = ['tenancy-parking', tenancyId];
  const q = useQuery({ queryKey: key, queryFn: () => parkingApi.forTenancy(tenancyId) });
  const [allocating, setAllocating] = useState(false);
  const [releasing, setReleasing] = useState(false);
  const [form, setForm] = useState({ bayId: '', vehicleReg: '', vehicle: '' });
  const free = useQuery({ queryKey: ['parking-free', propertyId], queryFn: () => parkingApi.bays({ propertyId, status: 'FREE' }), enabled: allocating });
  const refresh = () => { void qc.invalidateQueries({ queryKey: key }); void qc.invalidateQueries({ queryKey: ['parking', propertyId] }); void qc.invalidateQueries({ queryKey: ['/checklists', tenancyId] }); };
  const allocate = useMutation({
    mutationFn: () => parkingApi.allocate(form.bayId, { tenancyId, vehicleReg: form.vehicleReg || null, vehicle: form.vehicle || null }),
    onSuccess: () => { setAllocating(false); setForm({ bayId: '', vehicleReg: '', vehicle: '' }); refresh(); },
  });
  const release = useMutation({ mutationFn: () => parkingApi.release(q.data!.bay!.id, { toDate: today() }), onSuccess: () => { setReleasing(false); refresh(); } });
  const p = q.data;
  const a = p?.allocation;
  const choices = (free.data ?? []).filter((b) => b.allocatable);

  return (
    <Card title="Parking" actions={p && (p.bay
      ? <Can do="parking.release"><Button size="sm" variant="secondary" onClick={() => setReleasing(true)}>Release</Button></Can>
      : live && p.propertyHasBays ? <Can do="parking.allocate"><Button size="sm" onClick={() => setAllocating(true)}>Allocate</Button></Can> : null)}>
      {!p ? <SkeletonRows rows={2} cols={2} /> : p.bay && a ? (
        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <div className="grid h-12 w-12 shrink-0 place-items-center rounded-xl bg-bronze-50 text-[18px] font-bold text-bronze-700">{p.bay.code}</div>
            <div className="min-w-0 text-[12.5px] text-ink-600"><div className="font-medium text-ink-900">Bay {p.bay.code} · {p.bay.kindLabel}</div>{p.plan?.name ?? 'Not on a plan'}</div>
          </div>
          <KeyValue items={[
            { label: 'Since', value: <DateText value={a.fromDate} /> },
            { label: 'Vehicle', value: a.vehicleReg ? `${a.vehicleReg}${a.vehicle ? ` · ${a.vehicle}` : ''}` : '—' },
            { label: 'Permit', value: a.permit ?? '—' },
          ]} />
          {p.plan && <PlanSnippet imageUrl={p.plan.imageUrl} widthPx={p.plan.widthPx} heightPx={p.plan.heightPx} box={p.bay} code={p.bay.code} />}
          <Link to={`/app/properties/${propertyId}?tab=parking`} className="inline-block text-[12px] font-medium text-bronze-600 hover:underline">Open the car park</Link>
        </div>
      ) : (
        <div className="flex items-start gap-3 text-[12.5px] text-ink-500">
          <Car className="mt-0.5 h-4 w-4 shrink-0 text-ink-300" />
          <p>{p.propertyHasBays ? 'No bay allocated.' : 'This building has no car park plan yet.'}{p.history.length > 0 && <> Last held bay {p.history[0]!.bayCode} until <DateText value={p.history[0]!.toDate} />.</>}</p>
        </div>
      )}

      <Modal open={allocating} onClose={() => setAllocating(false)} title="Allocate a bay" footer={<>
        <Button variant="secondary" onClick={() => setAllocating(false)}>Cancel</Button>
        <Button onClick={() => allocate.mutate()} disabled={!form.bayId || allocate.isPending}>{allocate.isPending ? 'Allocating…' : 'Allocate'}</Button>
      </>}>
        <div className="space-y-4">
          <Field label="Bay" hint={free.isLoading ? 'Loading free bays…' : `${choices.length} free bay${choices.length === 1 ? '' : 's'} on this building's car park`}>
            <Select value={form.bayId} onChange={(e) => setForm({ ...form, bayId: e.target.value })} className="w-full">
              <option value="">Pick a bay</option>
              {choices.map((b) => <option key={b.id} value={b.id}>Bay {b.code} · {b.kindLabel}</option>)}
            </Select>
          </Field>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Vehicle registration"><Input value={form.vehicleReg} onChange={(e) => setForm({ ...form, vehicleReg: e.target.value.toUpperCase() })} placeholder="e.g. LP21 XYZ" className="w-full" /></Field>
            <Field label="Make / model / colour"><Input value={form.vehicle} onChange={(e) => setForm({ ...form, vehicle: e.target.value })} placeholder="Optional" className="w-full" /></Field>
          </div>
          <p className="text-[11.5px] text-ink-400">The bay shows on the resident's own page and is marked on the car park plan, so it is not given to anyone else.</p>
          {allocate.isError && <p className="text-[12px] text-red-600">{(allocate.error as Error).message}</p>}
        </div>
      </Modal>
      <Confirm open={releasing} onClose={() => setReleasing(false)} title={`Release bay ${p?.bay?.code ?? ''}?`} body="The bay becomes free from today. The allocation stays in the history." confirmLabel="Release bay"
        pending={release.isPending} error={release.error} onConfirm={() => release.mutate()} />
    </Card>
  );
}
